import { Controller, FormProvider, useForm } from 'react-hook-form';
import FlagsModel from '@/modules/authentication/sign-up/components/CountryFlags';
import { SelectCountryIconDropdown } from '@/components/form-inputs/select-country/SelectCountryIconDropdown';
import { yupResolver } from '@hookform/resolvers/yup';
import IdGeneratorService from '@/services/id-generator.service';
import { CREATE_SINGLE_PEOPLE_FORM } from '@/constants/forms';
import { v4 as uuidv4 } from 'uuid';
import { Column, Row } from '@/components/container/Container';
import { TextBodySmall, TextHeadlineExtraSmall, TextHeadlineMedium, TextLabelExtraSmall, TextTitleMedium2 } from '@/components/text/Texts';
import { cn } from '@/components/ui/cn';
import { showNotification } from '@/modules/notification/engine/notification.library';
import { BaseResponse, GenericResponse, ResponseErrorCode, TestLinkResponse } from '@/common/common_utils';
import { axiosInstance } from '@/services/axios.service';
import { AxiosResponse } from 'axios';
import { ENDPOINTS } from '@/constants/endpoints';
import { CreateEmployeeRequest } from '../views/AddPeopleScreen';
import { Button } from 'primereact/button';
import { People } from '../engine/People';
import { PhoneHelper } from '@/common/PhoneHelper';
import { InputController } from '@/components/inputs/InputController';
import DropdownInput from '@/components/inputs/DropdownInput';
import TextInput from '@/components/inputs/TextInput';

export const ROLE_OPTIONS = [
	{ value: 1, label: 'Admin' },
	{ value: 2, label: 'Manager' },
	{ value: 3, label: 'User' },
];

interface Props {
	groupId?: number;
	handleSelfClose: () => void;
}

const SingleAddPeopleForm = ({ groupId, handleSelfClose }: Props) => {
	const formName = new IdGeneratorService(CREATE_SINGLE_PEOPLE_FORM).formId || '';
	const form = useForm<People.EmployeeForm>({
		resolver: yupResolver(People.employeeFormSchema),
		mode: 'all'
	});

	const onSubmit = async () => {
		const values = form.getValues();
		const request: CreateEmployeeRequest = {
			XpressId: uuidv4(),
			FirstName: values.FirstName,
			LastName: values.LastName,
			Email: values.Email,
			RoleIds: [values.RoleId],
			ReferenceCode: values.ReferenceCode,
			Phone: PhoneHelper.preparePhoneNumber(values.Phone, values.Country?.code || 'PH'),
			GroupId: groupId,
		};
		const { data: response } = await axiosInstance.post<
			GenericResponse<TestLinkResponse>,
			AxiosResponse<GenericResponse<TestLinkResponse>>,
			CreateEmployeeRequest
		>(ENDPOINTS['/api/X4bEmployee/CreateEmployee'], request);

		if (response.ErrorCode == ResponseErrorCode.PhoneExists)
			form.setError('Phone', { type: 'server', message: response.ErrorMessage });

		console.log(`Employee invite link: ${response.Data.Link}`);

		handleSelfClose();
		showNotification(groupId === undefined
			? 'Employee added successfully'
			: 'Employee added to the group successfully.'
		);
	};

	return (
		<FormProvider {...form}>
			<form id={formName}>
				{groupId !== undefined && (
					<TextTitleMedium2
						text="Fill out the information below to add your employee"
						className="mb-4 text-onSurfaceVariant"
					/>)}
				<Column>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
						<InputController id="FirstName" control={form.control}>
							<TextInput label="First Name*" />
						</InputController>
						<InputController id="LastName" control={form.control}>
							<TextInput label="Last Name*" />
						</InputController>
					</div>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
						<InputController id="Email" control={form.control}>
							<TextInput label="Email*" type="email" />
						</InputController>
						<InputController id="RoleId" control={form.control}>
							<DropdownInput options={ROLE_OPTIONS} label="Role*" />
						</InputController>
					</div>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
						<InputController id="ReferenceCode" control={form.control}>
							<TextInput label="ID" keyfilter="num" />
						</InputController>
					</div>
					<TextLabelExtraSmall className="py-4 text-black" text="Required form labels ends with an *" />
				</Column>

				<div className="w-full h-[1px] bg-neutralVariant95 my-4" />

				<div className={cn('grid gap-2 mb-8', groupId !== undefined ? 'w-[626px]' : 'grid-cols-1 md:grid-cols-2')}>
					<Column className="gap-4">
						<Column className="gap-2">
							<Row className="gap-2.5 items-center">
								<TextHeadlineMedium className="text-neutral17" text="Add a Mobile Number" />
								<TextHeadlineExtraSmall className="text-neutral50" text="(Recommended)" />
							</Row>
							<TextBodySmall
								className="text-onSurfaceVariant"
								text="Experience faster activation by adding a mobile number. If the number is associated with a Xpress account, Xpress will send an invite directly via the Xpress app and/or text messages."
							/>
						</Column>

						<Row className="gap-6">
							<Controller
								name={'Country'}
								control={form.control}
								defaultValue={FlagsModel.flagByCountryCode('PH')}
								render={({ field }) => (<SelectCountryIconDropdown value={field.value} onChange={field.onChange} />)}
							/>
							<InputController id="Phone" control={form.control}>
								<TextInput label="Phone Number*" keyfilter="num" />
							</InputController>
						</Row>
					</Column>
				</div>

				<Button
					id="send-invite"
					label="Send Invite"
					onClick={form.handleSubmit(onSubmit)}
					pt={{
						root: { className: cn('h-12 w-[320px] mb-5 rounded-[100px] duration-300', form.formState.isValid ? 'bg-primary' : 'bg-neutral80 pointer-events-none') },
						label: { className: cn('font-poppins text-label-large2', form.formState.isValid ? 'text-white' : 'text-[#949494]') },
					}}
				/>

				<TextBodySmall
					className="text-onSurfaceVariant"
					text="If you add a phone number or email address associated with Xpress account, Xpress will also send an invite directly via the Xpress app and/or text messages (in addition to email)."
				/>
			</form>
		</FormProvider>
	);
};

export default SingleAddPeopleForm;
